/**
 * Dialog for setting up customer billing (reseller mode) on a property.
 * Installer sets price, customer email, optional trial period.
 * Shows fee breakdown and sends billing link via Checkout Session.
 */
import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Copy, Check } from 'lucide-react';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { AccountTier } from '@/contexts/AppContext';

const BASE_PRICES_CENTS: Record<string, number> = {
  monitor: 400,
  alert: 700,
  predict: 1500,
};

/** Default per-operator platform margin: 400 bps = 4%. */
const DEFAULT_PLATFORM_MARGIN_BPS = 400;

interface ResellerSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  propertyName: string;
  /** Plant tiers in this property, for computing the tier-license portion of the fee */
  plantTiers: (AccountTier | undefined)[];
  defaultPriceCents: number | null;
  defaultEmail?: string;
  /** Operator's platform margin in basis points (400 = 4%). Defaults to 4%. */
  marginBps?: number;
  onSuccess?: () => void;
}

export function ResellerSetupDialog({
  open,
  onOpenChange,
  propertyId,
  propertyName,
  plantTiers,
  defaultPriceCents,
  defaultEmail,
  marginBps = DEFAULT_PLATFORM_MARGIN_BPS,
  onSuccess,
}: ResellerSetupDialogProps) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [priceDollars, setPriceDollars] = useState(
    defaultPriceCents ? (defaultPriceCents / 100).toFixed(2) : ''
  );
  const [trialDays, setTrialDays] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const tierFeeCents = useMemo(() => {
    return plantTiers.reduce((sum, tier) => {
      const t = tier || 'alert';
      return sum + (BASE_PRICES_CENTS[t] || BASE_PRICES_CENTS.alert);
    }, 0);
  }, [plantTiers]);

  const priceCents = Math.round(parseFloat(priceDollars || '0') * 100);
  const safeMarginBps = Math.min(Math.max(marginBps, 0), 2500);
  const marginFeeCents = priceCents > 0 ? Math.round((priceCents * safeMarginBps) / 10000) : 0;
  const platformFeeCents = tierFeeCents + marginFeeCents;
  const isValid = email.includes('@') && priceCents > platformFeeCents;
  const installerPayoutCents = Math.max(0, priceCents - platformFeeCents);

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);
    try {
      const data = await invokeAuthedFunction<{
        checkout_url?: string;
        error?: string;
        platform_fee_cents?: number;
      }>(supabase, 'create-customer-subscription', {
        property_id: propertyId,
        price_cents: priceCents,
        customer_email: email,
        trial_days: trialDays ? parseInt(trialDays, 10) : undefined,
      });
      if (data?.checkout_url) {
        setCheckoutUrl(data.checkout_url);
        toast.success('Billing link created');
        onSuccess?.();
      } else {
        toast.error(data?.error || 'Failed to create billing link');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create billing link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!checkoutUrl) return;
    await navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    toast.success('Billing link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setCheckoutUrl(null);
      setCopied(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Up Customer Billing</DialogTitle>
          <DialogDescription>
            Bill your customer directly for {propertyName}. Watt Tower's platform fee is automatically deducted.
          </DialogDescription>
        </DialogHeader>

        {checkoutUrl ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Share this link with your customer to complete payment setup:
            </p>
            <div className="flex gap-2">
              <Input value={checkoutUrl} readOnly className="text-xs font-mono" />
              <Button variant="outline" size="icon" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="customer-email">Customer email</Label>
              <Input
                id="customer-email"
                type="email"
                placeholder="customer@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="monthly-price">Monthly price ($)</Label>
              <Input
                id="monthly-price"
                type="number"
                step="0.01"
                min={(platformFeeCents / 100 + 0.01).toFixed(2)}
                placeholder={(platformFeeCents / 100 + 5).toFixed(2)}
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="trial-days">Trial period (days, optional)</Label>
              <Input
                id="trial-days"
                type="number"
                min="0"
                max="90"
                placeholder="0"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
              />
            </div>

            {/* Fee breakdown */}
            <div className="rounded-lg border border-border bg-wt-bg-secondary p-3 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Customer pays</span>
                <span className="font-mono text-foreground">
                  ${(priceCents / 100).toFixed(2)}/mo
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">License fee ({plantTiers.length} system{plantTiers.length !== 1 ? 's' : ''})</span>
                <span className="font-mono text-muted-foreground">
                  -${(tierFeeCents / 100).toFixed(2)}/mo
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Platform margin ({(safeMarginBps / 100).toFixed(safeMarginBps % 100 === 0 ? 0 : 2)}%)</span>
                <span className="font-mono text-muted-foreground">
                  -${(marginFeeCents / 100).toFixed(2)}/mo
                </span>
              </div>
              <div className="border-t border-border pt-1.5 flex justify-between">
                <span className="text-muted-foreground font-medium">You receive</span>
                <span className="font-mono text-wt-green-primary font-medium">
                  ${(installerPayoutCents / 100).toFixed(2)}/mo
                </span>
              </div>
              {priceCents > 0 && priceCents <= platformFeeCents && (
                <p className="text-xs text-destructive">
                  Price must exceed total platform fee of ${(platformFeeCents / 100).toFixed(2)}/mo
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {checkoutUrl ? (
            <Button variant="outline" onClick={() => handleClose(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!isValid || loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Send Billing Link'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
